import { ChangeDetectorRef, Component, inject, OnInit, signal } from "@angular/core";
import { isStringNullOrEmpty } from "@globals/helper-functions";
import { MessageStatusDetailsReadModel } from "@globals/openapi-models/model/messageStatusDetailsReadModel";
import { BiAddressServiceBase } from "@globals/services/address-base.service";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { BiAddressInfoTableComponentData } from "./bi-address-info-table/bi-address-info-table.component";

export interface AddressInfoComponentInputs<T> {
  address: T;
  enableDeselection?: boolean;
  isStatus: boolean;
  testMode?: boolean;
  smsGroupId?: number;
  /**
   * Include info about company name and CVR. This data must only be returned by server if profile is allowed.
   */
  includeCompanyInfo: boolean;

  deleteAddress?: () => void;
  deleteProperty?: () => void;
}

/**
 * Base component for showing address info. The type of "T" is the address model type.
 */
@Component({
    selector: "bi-address-info-base",
    template: "",
    standalone: false
})
export abstract class AddressInfoBaseComponent<T> implements OnInit {
  dialogInputData: AddressInfoComponentInputs<T>;
  public address: T;

  /**
   * List of objects containg info about the address as well as person name and phone number and/or email. This
   * is only to be used if "isStatus" is false.
   */
  public privateRecipientDetails: Array<BiAddressInfoTableComponentData>;
  public phoneRecipientsDetails: Array<BiAddressInfoTableComponentData>;
  public emailRecipientsDetails: Array<BiAddressInfoTableComponentData>;
  public eboksRecipientsDetails: Array<BiAddressInfoTableComponentData>;
  public companies: Array<BiAddressInfoTableComponentData>;

  //==== Callback for when "deselect address" is clicked
  deleteAddress?: () => void;
  deleteProperty?: () => void;
  //====

  public isLoading = signal(true);
  public isTestMode = signal(false);

  public alignTables = signal(false);

  public readonly dialogRef = inject(DynamicDialogRef);
  public readonly dialogConfig = inject(DynamicDialogConfig);
  protected readonly cd = inject(ChangeDetectorRef);
  protected readonly addressService = inject(BiAddressServiceBase);

  ngOnInit() {
    this.dialogInputData = this.dialogConfig.data as AddressInfoComponentInputs<T>;
    this.address = this.dialogInputData.address;
    this.deleteAddress = this.dialogInputData.deleteAddress;
    this.deleteProperty = this.dialogInputData.deleteProperty;
  }

  protected abstract getAddressInfo(): void;

  protected setRecipientDetails(details: Array<MessageStatusDetailsReadModel>, includeEboksDetails: boolean) {
    this.privateRecipientDetails = details.map(
      d => <BiAddressInfoTableComponentData>{ name: d.personName, phoneCode: d.phoneCode, phoneNumber: d.phoneNumber, email: d.email, status: d.statusMessage, isSubscription: d.isSubscription }
    );
    this.emailRecipientsDetails = this.privateRecipientDetails.filter(a => !isStringNullOrEmpty(a.email));
    this.phoneRecipientsDetails = this.privateRecipientDetails.filter(a => a.phoneNumber != undefined);
    if (includeEboksDetails) this.eboksRecipientsDetails = this.privateRecipientDetails.filter(a => a.status.indexOf("e-Boks") > -1);
  }

  protected checkIfThereAreMultipleTablesToAlign() {
    let numberOfTables = 0;
    if (this.companies?.length > 0) ++numberOfTables;
    if (this.emailRecipientsDetails.length > 0) ++numberOfTables;
    if (this.eboksRecipientsDetails?.length > 0) ++numberOfTables;
    if (this.phoneRecipientsDetails.length > 0) ++numberOfTables;

    this.alignTables.set(numberOfTables > 1);
  }
}
